import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { deleteBatch, fetchBatchImage, generateBatchImage, getBatch } from '../api'
import { StageList, useGenerationProgress } from '../components/ProgressStages'
import { celebrateBurst as celebrate } from '../components/GameFx'
import type { IdeaBatch } from '../types'

const FIELD_LABELS: Array<{ key: keyof IdeaBatch['ideas'][number]; label: string }> = [
  { key: 'name', label: '游戏名称' },
  { key: 'genre', label: '游戏类型' },
  { key: 'coreGameplay', label: '核心玩法' },
  { key: 'coreLoop', label: '核心游戏循环' },
  { key: 'innovation', label: '创新亮点' },
  { key: 'playerGoal', label: '玩家目标' },
  { key: 'developmentDifficulty', label: '开发难度' },
  { key: 'visualTheme', label: '视觉主题' },
  { key: 'imagePrompt', label: '生图提示词' },
]

export default function ResultPage() {
  const { id } = useParams()
  const location = useLocation()
  const navState = location.state as { autoImage?: boolean; batch?: IdeaBatch } | null
  const autoImage = Boolean(navState?.autoImage)
  const skipLoading = Boolean(navState?.autoImage)
  const [batch, setBatch] = useState<IdeaBatch | null>(navState?.batch ?? null)
  const [imgs, setImgs] = useState<Record<number, string | null>>({})
  const [imgLoading, setImgLoading] = useState<Record<number, boolean>>({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(!navState?.batch)
  const autoRanRef = useRef(false)
  const urlsRef = useRef<string[]>([])
  const { stages, elapsedMs, setStage, markFrom, startTimer, stopTimer } = useGenerationProgress()

  const loadImg = useCallback(async (batchId: string, ideaIndex: number) => {
    setImgLoading((m) => ({ ...m, [ideaIndex]: true }))
    const url = await fetchBatchImage(batchId, ideaIndex)
    if (url) urlsRef.current.push(url)
    setImgs((m) => ({ ...m, [ideaIndex]: url }))
    setImgLoading((m) => ({ ...m, [ideaIndex]: false }))
  }, [])

  /** 自动为 0/1/2 各生一张，逐张更新进度 */
  const runAllImages = useCallback(
    async (batchId: string) => {
      startTimer()
      markFrom('draw')
      const results: string[] = []
      for (let i = 0; i < 3; i++) {
        if (i > 0) await new Promise((r) => setTimeout(r, 1200))
        const tag = String.fromCharCode(65 + i)
        setStage('draw', 'active', `正在生成方案 ${tag} 的概念图（${i + 1}/3）`)
        setImgLoading((m) => ({ ...m, [i]: true }))
        try {
          const result = await generateBatchImage(batchId, i)
          if (result.ok) {
            setStage('encode', 'active', `方案 ${tag} → JPEG 90%`)
            setStage('encode', 'done')
            setStage('persist', 'active', `保存方案 ${tag} 图片`)
            await loadImg(batchId, i)
            setStage('persist', 'done')
            results.push(`${tag} 已出图`)
          } else if (result.error && (result.error.includes('正在生成中') || result.error.includes('已在生成'))) {
            // 等一会儿再试一次
            await new Promise((r) => setTimeout(r, 4000))
            const retry = await generateBatchImage(batchId, i, 1)
            if (retry.ok) {
              await loadImg(batchId, i)
              setImgLoading((m) => ({ ...m, [i]: false }))
              results.push(`${tag} 已出图`)
            } else {
              setImgLoading((m) => ({ ...m, [i]: false }))
              results.push(`${tag} 失败`)
              setError((e) => (e ? e + '；' : '') + (retry.error || `方案 ${tag} 生图失败`))
            }
          } else {
            setImgLoading((m) => ({ ...m, [i]: false }))
            results.push(`${tag} 失败`)
            setError((e) => (e ? e + '；' : '') + (result.error || `方案 ${tag} 生图失败`))
          }
        } catch (err) {
          setImgLoading((m) => ({ ...m, [i]: false }))
          results.push(`${tag} 异常`)
          setError((e) => (e ? e + '；' : '') + (err instanceof Error ? err.message : '生图失败'))
        }
      }
      setStage('draw', 'done', results.join('，'))
      setStage('persist', 'done')
      setStage('done', 'done')
      celebrate()
      stopTimer()
      const b = await getBatch(batchId).catch(() => null)
      if (b) setBatch(b)
    },
    [loadImg, markFrom, setStage, startTimer, stopTimer],
  )

  useEffect(() => {
    if (!id) return
    setLoading(!batch && !skipLoading)
    const loadWithRetry = async (attempt = 0): Promise<void> => {
      try {
        const b = await getBatch(id)
        setBatch(b)
        for (let i = 0; i < 3; i++) {
          if (b.imageStatus === 'ok' || b.imageStatus === 'partial' || b.imageStatus === 'pending') {
            await loadImg(b.id, i)
          }
        }
      } catch (err) {
        if (attempt < 3) {
          await new Promise((r) => setTimeout(r, 700 * (attempt + 1)))
          return loadWithRetry(attempt + 1)
        }
        setError(err instanceof Error ? err.message : '加载失败')
      }
    }
    loadWithRetry()
  }, [id, loadImg])


  useEffect(() => {
    if (!id || !autoImage || autoRanRef.current || !batch) return
    if (batch.imageStatus === 'ok' || batch.imageStatus === 'partial' || batch.imageStatus === 'pending') {
      return
    }
    autoRanRef.current = true
    void runAllImages(id)
  }, [id, autoImage, batch, runAllImages])

  async function onDelete() {
    if (!id || !window.confirm('删除这条历史（含概念图）？此操作不可恢复。')) return
    try {
      await deleteBatch(id)
      window.location.href = '/history'
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败')
    }
  }

  if (loading && !batch) {
    return (
      <section className="section">
        <div className="loading-row">
          <span className="spinner" />
          读取创意批次…
        </div>
      </section>
    )
  }

  if (!batch) {
    return (
      <section className="section">
        {error ? (
          <div className="alert alert-error">{error}</div>
        ) : (
          <div className="loading-row">
            <span className="spinner" />
            正在载入方案…
          </div>
        )}
        <Link className="btn btn-secondary" to="/history">
          返回历史
        </Link>
      </section>
    )
  }

  return (
    <section className="section">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ marginBottom: '0.35rem' }}>三个游戏创意</h2>
          <div className="meta">
            <span className="chip">{new Date(batch.createdAt).toLocaleString('zh-CN')}</span>
            <span className="chip">自动生成 3 张展示图</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
          <Link className="btn btn-primary" to="/create">
            再生成一批
          </Link>
          <Link className="btn btn-ghost" to="/history">
            历史记录
          </Link>
          <button type="button" className="btn btn-secondary" onClick={onDelete}>
            删除本条
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-warning">{error}</div> : null}

      <StageList stages={stages} elapsedMs={elapsedMs} />

      <div className="block">
        <h3>原始构想</h3>
        <p className="muted">{batch.input}</p>
      </div>

      <div className="cards" style={{ gridTemplateColumns: '1fr' }}>
        {batch.ideas.map((idea, index) => (
          <article className="card idea-card" key={index}>
            <div className="idea-head">
              <span className="chip">方案 {String.fromCharCode(65 + index)}</span>
              <h3>{idea.name}</h3>
              <span className="chip">{idea.genre}</span>
            </div>
            <dl className="idea-fields">
              {FIELD_LABELS.filter((f) => f.key !== 'name' && f.key !== 'genre').map(({ key, label }) => (
                <div key={key}>
                  <dt>{label}</dt>
                  <dd>{idea[key]}</dd>
                </div>
              ))}
            </dl>
            <div
              className="image-frame image-frame-adapt"
              style={
                imgs[index]
                  ? { aspectRatio: '16 / 9' }
                  : undefined
              }
            >
              {imgs[index] ? (
                <img
                  src={imgs[index] as string}
                  alt={`${idea.name} 概念图`}
                  onLoad={(e) => {
                    const img = e.currentTarget
                    const ratio = img.naturalWidth / img.naturalHeight
                    img.parentElement?.style.setProperty('aspect-ratio', String(ratio))
                  }}
                />
              ) : imgLoading[index] ? (
                <div className="loading-row">
                  <span className="spinner" />
                  概念图生成中…
                </div>
              ) : (
                <span className="muted">暂无概念图</span>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}
